import type { ItemLocation } from '@teo/backend';
import { DISPLAY_ID_PATTERN } from '@teo/types';

const DISPLAY_ID = new RegExp(`^${DISPLAY_ID_PATTERN}$`);

/**
 * Where an item opens in Teo, by the prefix of its display id. Properties,
 * portfolios and ownerships have no page of their own, so they open at their
 * card on the page that lists them.
 */
const ITEM_PAGES: Readonly<Record<string, (displayId: string) => string>> = {
  DOCM: (displayId) => `/document/${displayId}`,
  CNTR: (displayId) => `/contract/${displayId}`,
  INVC: (displayId) => `/invoice/${displayId}`,
  RFPR: (displayId) => `/rfp/${displayId}`,
  PRPS: (displayId) => `/proposal/${displayId}`,
  CMPS: (displayId) => `/comparison/${displayId}`,
  BUDG: (displayId) => `/budgets?budgetDisplayId=${displayId}`,
  PROP: (displayId) => `/properties#property-${displayId}`,
  PORT: (displayId) => `/properties#portfolio-${displayId}`,
  OWND: (displayId) => `/ownerships#ownership-${displayId}`,
};

/**
 * Items listed on pages scoped to the ownership chosen in the header. Their
 * page is known only once the server has said which ownership, and for some
 * which property, the item belongs to.
 */
const LOCATED_PREFIXES: ReadonlySet<string> = new Set([
  'UNIT',
  'IPMO',
  'VEND',
  'INCS',
  'EXPS',
  'BPLN',
  'LOAN',
]);

/** Whether a link target is a display id, as the agent links an item. */
export function isDisplayId(href: string): boolean {
  return DISPLAY_ID.test(href);
}

/** The page where an item opens, or undefined for an item that has no page. */
export function itemPath(displayId: string): string | undefined {
  return ITEM_PAGES[displayId.slice(0, 4)]?.(displayId);
}

/** Whether the item's page depends on where it lives, so the server must locate it first. */
export function needsLocation(displayId: string): boolean {
  return LOCATED_PREFIXES.has(displayId.slice(0, 4));
}

/** The page where a located item opens, within its ownership. */
export function locatedPath(location: ItemLocation): string {
  switch (location.kind) {
    case 'unit':
      return `/ar-invoices?propertyId=${location.propertyId}&tab=recurring#unit-${location.unitId}`;
    case 'arInvoice':
      return `/ar-invoices?propertyId=${location.propertyId}&occurrence=${location.occurrenceId}`;
    case 'vendor':
      return `/invoices/${location.ownershipId}?vendor=${location.vendorId}`;
    case 'incomeCommitment':
      return `/commitments/income#income-${location.scheduleId}`;
    case 'receivableSchedule':
      return location.propertyId === undefined
        ? '/ar-invoices'
        : `/ar-invoices?propertyId=${location.propertyId}&tab=recurring`;
    case 'expenseSchedule':
      return `/expenses?schedule=${location.scheduleId}`;
    case 'budgetPlan':
      return `/budget-planning?fiscalYear=${location.fiscalYear}`;
    case 'loan':
      return `/expenses/loans?loan=${location.loanId}`;
  }
}
